import {mutation, query} from "@/graphql/client";
import {gql} from "@urql/vue";
import {AInesJobsiteListType, House, Jobsite, JobsiteFragmentFragment, JobsiteType} from "@/graphql/generated/graphql";
import {JobsiteCategory} from "@/graphql/kapitelTypes";
import {listFormat, pluralize, pluralizeUnitOnly} from "./amountFormatter";
import {graphql} from "@/graphql/generated";

graphql(`fragment JobsiteThin on Jobsite {
    id,
    name,
    abbreviation,
    additionalAmenImportantAbbreviations,
    additionalAmenImportantLabels,
    isFavorite,
    isAlternative,
    disabledEffectively
}`)

export const jobsiteFragment = graphql(`fragment JobsiteFragment on Jobsite {
    ...JobsiteThin
    client {
        ...ClientFragment
    }
    house {
        ...HouseFragment
    }
}`)

export const jobsiteDetailsFragment = graphql(` fragment JobsiteDetailsFragment on Jobsite {
    ...JobsiteFragment
    about,
    requirements
    fachbereiche {
        name,
        abbreviation
    }  
    qualificationAbout
    medicalAbout
    bedCount
    bedTypes
    careRatio
    shiftTimes
    equipment
    jobsiteType {
        name
    }
    enrichedAbout
    retrieved
    manuallyCurated    
}`)
export const clientFragment = graphql(`fragment ClientFragment on Client {
    name
    abbreviation
    logo
    imageURL
}`)
export const houseFragment = graphql(`fragment HouseFragment on House {
    name
    abbreviation
    imageURL
}`)
export const addressFragment = graphql(`fragment AddressFragment on Address {
    name
    company
    street
    district
    postalCode
    city
    country
}`)
export const contactFragment = graphql(`fragment ContactFragment on Contact {
    name
    phone
    mobile
    mail
}`)

export const getImageForJobsite = (jobsite: Jobsite | undefined) => {
    return jobsite?.house?.imageURL || jobsite?.client?.imageURL || '/clients/generative.jpg'
}

export async function fetchJobsites(queryStr = '', offset = 0, count = 25, excludeJobsites : Jobsite[] = [], jobsiteTypeFilter: JobsiteType[]|undefined = undefined): Promise<any> {
    const result = await query(graphql(`
        
        
        
        query QueryJobsites ($query: String, $pagination: Int, $pageSize: Int, $excludeJobsiteIds: [Int!], $jobsiteTypeFilter: [JobsiteTypeInput!]) {
            jobsitesByQuery (query: $query, pagination: $pagination, pageSize: $pageSize, excludeJobsiteIds: $excludeJobsiteIds, jobsiteTypeInputFilter: $jobsiteTypeFilter) {
                jobsite {
                    ...JobsiteFragment
                    client {
                        ...ClientFragment
                    }
                    house {
                        ...HouseFragment
                    }
                }
                isLastItem
              }
        }`),
        {
            query: queryStr,
            pagination: offset,
            pageSize: count,
            excludeJobsiteIds: excludeJobsites.map((j) => j.id),
            jobsiteTypeFilter: jobsiteTypeFilter
        }
    )

    return result?.data?.jobsitesByQuery
}


export const getJobsitesByType = async (listType: AInesJobsiteListType): Promise<any> => {
    switch (listType){
        case AInesJobsiteListType.Favorite:
            return fetchFavoriteJobsites()
        case AInesJobsiteListType.Alternative:
            return fetchSecondChoiceJobsites()
        default:
            throw new Error("Unable to fetch this jobsite list type.")
    }
}

export async function fetchFavoriteJobsites(): Promise<any> {
    const result = await query(graphql(`
        
        
        
        query GetFavoriteJobsites{
              favoriteJobsites {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                }
                house {
                    ...HouseFragment
                }
              }
        }`)
    )

    return result?.data?.favoriteJobsites
}

export async function fetchSecondChoiceJobsites(): Promise<any> {
    const result = await query(graphql(`
        
        
        
        query GetSecondChoiceJobsites{
            secondChoiceJobsites {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                }
                house {
                    ...HouseFragment
                }
              }
        }`)
    )

    return result?.data?.secondChoiceJobsites
}

export async function fetchBlacklistedJobsites(): Promise<any> {
    const result = await query(graphql(`
        
        
        
        query GetBlacklistedJobsites{
            blacklistJobsites {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                }
                house {
                    ...HouseFragment
                }
              }
        }`)
    )

    return result?.data?.blacklistJobsites
}

export const fetchJobsiteDetail = async (jobsiteId: number): Promise<Jobsite|undefined>=> {

    const result = await query(graphql(`
        
        
        
        
        
        query GetJobsiteDetail ($idDings: Int!){
            jobsite (
            id: $idDings
            ){
                ...JobsiteDetailsFragment
                client {
                    ...ClientFragment
                    address {...AddressFragment}
                },
                house {
                    ...HouseFragment
                    address {...AddressFragment}
                }
                address {...AddressFragment},
                contact {...ContactFragment}
            }
        }`),
        {
            idDings: jobsiteId
        }
    )

    return result?.data?.jobsite || undefined
}

export const getJobsite = async (id: number): Promise<Jobsite|undefined> => {

    const result = await query(
        graphql(`
            query GetJobsite (
                $id: Int!
            ){
              jobsite (
                id: $id
              ){
                  ...JobsiteFragment
              }
        }`),
        {
            id
        }
    )

    return result?.data?.jobsite
}


export const getJobsites = async (ids: Array<number>): Promise<any> => {

    const result = await query(
        graphql(`
            query GetJobsites (
                $ids: [Int!]!
            ){
              jobsites (
                ids: $ids
              ){
                  ...JobsiteFragment
              }
        }`),
        {
            ids: ids
        }
    )

    return result?.data?.jobsites
}

export const getJobsiteDetails = async (ids: Array<number>): Promise<any> => {

    const result = await query(graphql(`
        
        
        
        
        
        query GetJobsiteDetails (
            $ids: [Int!]!
        ){
            jobsites (
                ids: $ids
            ){
                ...JobsiteDetailsFragment
                client {
                    ...ClientFragment
                    address {...AddressFragment}
                },
                house {
                    ...HouseFragment
                    address {...AddressFragment}
                }
                address {...AddressFragment},
                contact {...ContactFragment}
            }
        }`),
        {
            ids: ids
        }
    )

    return result?.data?.jobsites
}



export const fetchJobsiteShiftTimes = async (jobsite: Jobsite) => {

    const result = await query(graphql(`
        query GetJobsiteShiftTimes (
            $jobsiteDings: Int!
        ){
            jobsiteShiftTimes (jobsite: { id: $jobsiteDings}) {
                type,
                begin,
                until
            }
        }`),
        {
            jobsiteDings: jobsite.id
        }
    )

    return result?.data?.jobsiteShiftTimes
}


// Mutations

export const addFavoriteJobsite = async (jobsite: Jobsite) => {
    const MUTATION = graphql(`
            
            
            
            mutation AddFavoriteJobsite (
                    $idDings: Int!,
            ) {
                addFavoriteJobsite(
                    id: $idDings
                )
                {
                    ...JobsiteFragment
                    client {
                        ...ClientFragment
                    },
                    house {
                        ...HouseFragment
                    }
                }
            }`)

    const result = await mutation(
        MUTATION, {
            idDings: jobsite.id,
            nameDings: jobsite.name
        }
    )

    return result?.data.addFavoriteJobsite
}

export const removeFavoriteJobsite = async (jobsite: Jobsite) => {
    const MUTATION = graphql(`
            
            
            
            mutation RemoveFavoriteJobsite (
                    $idDings: Int!,
            ) {
                removeFavoriteJobsite(
                id: $idDings,
                )
                {
                    ...JobsiteFragment
                    client {
                        ...ClientFragment
                    },
                    house {
                        ...HouseFragment
                    }
                }
            }`)

    const result = await mutation(
        MUTATION, {
            idDings: jobsite.id
        }
    )

    return result?.data.removeFavoriteJobsite
}

export const addSecondChoiceJobsite = async (jobsite: Jobsite) => {
    const MUTATION = graphql(`
        
        
        
        mutation AddSecondChoiceJobsite (
                $idDings: Int!,
        ) {
            addSecondChoiceJobsites(
                jobsiteId: $idDings
            )
            {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                },
                house {
                    ...HouseFragment
                }
            }
        }`)

    const result = await mutation(
    MUTATION, {
        idDings: jobsite.id,
        nameDings: jobsite.name
    }
    )

    return result?.data.addSecondChoiceJobsites
}

export const removeSecondChoiceJobsite = async (jobsite: Jobsite) => {
    const MUTATION = graphql(`
            
            
            
            mutation RemoveSecondChoiceJobsite (
                    $idDings: Int!,
            ) {
                removeSecondChoiceJobsites(
                    jobsiteId: $idDings,
                )
                {
                    ...JobsiteFragment
                    client {
                        ...ClientFragment
                    },
                    house {
                        ...HouseFragment
                    }
                }
            }`)

    const result = await mutation(
        MUTATION, {
            idDings: jobsite.id
        }
    )

    return result?.data.removeSecondChoiceJobsites
}

export const updateSecondChoiceJobsites = async (jobsiteIds: Array<number>) => {
    const MUTATION = graphql(`
        
        
        
        mutation UpdateSecondChoiceJobsites (
                $orderedJobsites: [Int!]!,
        ) {
            updateSecondChoiceJobsites(
                orderedJobsites: $orderedJobsites,
            )
            {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                },
                house {
                    ...HouseFragment
                }
            }
        }`)

    const result = await mutation(
        MUTATION, {
            orderedJobsites: jobsiteIds
        }
    )

    return result?.data.updateSecondChoiceJobsites
}

export const addBlacklistJobsite = async (jobsite: Jobsite) => {
    const MUTATION = graphql(`
        
        
        
        mutation AddBlacklistJobsite (
                $idDings: Int!,
        ) {
            addBlacklistJobsite(
                id: $idDings
            )
            {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                },
                house {
                    ...HouseFragment
                }
            }
        }`)

    const result = await mutation(
    MUTATION, {
        idDings: jobsite.id,
        nameDings: jobsite.name
    }
    )

    return result?.data.addBlacklistJobsite
}

export const removeBlacklistJobsite = async (jobsite: Jobsite) => {
    const MUTATION = graphql(`
            
            
            
            mutation RemoveBlacklistJobsite (
                    $idDings: Int!,
            ) {
                removeBlacklistJobsite(
                id: $idDings,
                )
                {
                    ...JobsiteFragment
                    client {
                        ...ClientFragment
                    },
                    house {
                        ...HouseFragment
                    }
                }
            }`)

    const result = await mutation(
        MUTATION, {
            idDings: jobsite.id
        }
    )

    return result?.data.removeBlacklistJobsite
}

export const fetchBookedJobsites = async (): Promise<Array<Jobsite>> => {

    const result = await query(graphql(`
        
        
        
        query GetBookedJobsites {
            bookedJobsites {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                }
                house {
                    ...HouseFragment
                }
            }
        }`)
    )

    return result.data.bookedJobsites
}

export const getJobsiteDescription = (jobsite: Jobsite, prefixOnly:boolean = false): string => {
    const result = []

    if (jobsite.client) {
        result.push(jobsite.client.name)
    }
    if (jobsite.house) {
        result.push(jobsite.house.name)
    }

    if (!prefixOnly) {
        result.push(jobsite.name)
    }

    return result.join(" > ")

}

export const getShortJobsiteDescription = (jobsite: JobsiteFragmentFragment, prefixOnly:boolean = false, joiner = " > "): string => {
    const result = []

    if (jobsite.client) {
        result.push(jobsite.client.abbreviation)
    }
    if (jobsite.house) {
        result.push(jobsite.house.abbreviation)
    }

    if (!prefixOnly) {
        result.push(jobsite.abbreviation)
    }

    return result.join(joiner)

}


export const getJobsiteShortestCompleteDescription = (jobsite: Jobsite, prefixOnly:boolean = false): string => {
    const result = []

    if (jobsite.client) {
        result.push(jobsite.client.abbreviation)
    }
    if (jobsite.house) {
        result.push(jobsite.house.abbreviation)
    }
    if(jobsite.additionalAmenImportantAbbreviations && jobsite.additionalAmenImportantAbbreviations.length>0){
        result.push(jobsite.additionalAmenImportantAbbreviations.join(" > "))
    }

    if (!prefixOnly) {
        result.push(jobsite.abbreviation)
    }

    return result.join(" > ")
}

// compare by id, not by object equality - lists contain jobsite objects from different sources
export const findJobsiteInList = (list: Array<Jobsite>, jobsite: Jobsite) => list.find(j => j.id === jobsite.id)
export const includesListJobsite = (list: Array<Jobsite>, jobsite: Jobsite) => list.some(j => j.id === jobsite.id)
export const indexOfJobsiteInList = (list: Array<Jobsite>, jobsite: Jobsite) => {
    const j = findJobsiteInList(list, jobsite)
    return j ? list.indexOf(j) : -1
}

export const getEmployeeJobsites = async () => {
    const [favorite,secondChoice,booked] : [Jobsite[],Jobsite[],Jobsite[]]= await Promise.all([
        fetchFavoriteJobsites(),
        fetchSecondChoiceJobsites(),
        fetchBookedJobsites()
    ])

    return {favorite, secondChoice, booked}
}
export const getEmployeeJobsiteCategorySegments = async () : Promise<JobsiteCategorySegment[]>  => {
    const {favorite, secondChoice, booked} = await getEmployeeJobsites()
    return [
        {category: JobsiteCategory.Favorite, jobsites: favorite},
        {category: JobsiteCategory.Alternative, jobsites: secondChoice},
        {category: JobsiteCategory.Booked, jobsites: booked}
    ]
}

export interface JobsiteCategorySegment {
    category: JobsiteCategory,
    jobsites: Jobsite[]
}

export const splitByJobsiteCategory = (jobsites: Jobsite[]) => {
     const favorite = {category: JobsiteCategory.Favorite, jobsites: [] as Jobsite[]}
    const alternative = {category: JobsiteCategory.Alternative, jobsites: [] as Jobsite[]}
    const booked = {category: JobsiteCategory.Booked, jobsites: [] as Jobsite[]}
    const other = {category: JobsiteCategory.Other, jobsites: [] as Jobsite[]}

    jobsites.forEach(jobsite => {
        if (jobsite.isFavorite) {
            favorite.jobsites.push(jobsite)
        } else if (jobsite.isAlternative) {
            alternative.jobsites.push(jobsite)
        } else if (jobsite.isBooked) {
            booked.jobsites.push(jobsite)
        } else {
            other.jobsites.push(jobsite)
        }
    })

    return {favorite, alternative, booked, other}

    // const result = [favorite, alternative, booked, other]

    // return result.filter(segment => segment.jobsites.length > 0)
}

export const pluralizedJobsiteAmount = (amount: number, lowercase = false) => {
    const word =`${lowercase ? 's' : 'S'}tation`
    return pluralize(amount, word, `${word}en`)
}

export const pluralizedJobsiteLabel = (amount: number, lowercase = false) => {
    const word =`${lowercase ? 's' : 'S'}tation`
    return pluralizeUnitOnly(amount, word, `${word}en`)
}

export const pluralizedHouseAmount = (amount: number, lowercase = false) => {
    const letter = lowercase ? 'h' : 'H'
    return pluralize(amount, `${letter}aus`, `${letter}äuser`)
}


export const jobsiteSummaryLabel = (jobsites: Jobsite[]) : string => {

    const favoriteCount = jobsites.filter(j => j.isFavorite).length
    const alternativeCount = jobsites.filter(j => j.isAlternative).length
    const remainingCount = jobsites.filter(j => !j.isAlternative && !j.isFavorite).length

    const components = [];

    if (favoriteCount > 0) {
        components.push(`${favoriteCount} Lieblings${(remainingCount + alternativeCount > 0) ? '-' : pluralizedJobsiteLabel(favoriteCount, true)}`)
    }
    if (alternativeCount > 0) {
        components.push(`${alternativeCount} Alternativ${(remainingCount > 0) ? '-' : pluralizedJobsiteLabel(alternativeCount, true)}`)
    }
    if (remainingCount > 0) {
        components.push(`${remainingCount} weitere ${pluralizedJobsiteLabel(remainingCount)}`)
    }

    const formatter = new Intl.ListFormat('de', { style: 'long', type: 'conjunction' });
    return formatter.format(components)
}

export const clientListLabelObject = (jobsites: Jobsite[]) => {

    // take houses where possible and clients from the rest
    let houses = jobsites.map((js) => js.house).filter((h):h is House => !!h)
    let clients = jobsites.filter((j) => !j.house).map(js => js.client)

    // unique
    houses = houses.filter((j, i) => houses.findIndex(j2 => j2.name === j.name) === i)
    clients = clients.filter((j, i) => clients.findIndex(j2 => j2.name === j.name) === i)

    const combined = houses.map(h => h.abbreviation || '').concat(clients.map(c => c.abbreviation || ''))

    return {
        houses,
        clients,
        combined
    }
}
export const clientListLabel = (jobsites: Jobsite[], maxEnumarationValues = 3) : string => {
    const rs = clientListLabelObject(jobsites)
    return rs.combined.length > 3
        ? pluralizedHouseAmount(rs.combined.length)
        : listFormat(rs.combined, {style: "short"}, 'en')
}

export const jobsiteListTitle = (jobsite:Jobsite, abbreviation = false) : string => {
    return (abbreviation ? jobsite.abbreviation : jobsite.name) ?? ''
}
export const jobsiteListSubtitle = (jobsite:Jobsite, abbreviation = false) : string => {
    let result = []
    if (jobsite.client) {
        result.push(abbreviation ? jobsite.client.abbreviation : jobsite.client.name)
    }
    if (jobsite.house) {
        result.push(abbreviation ? jobsite.house.abbreviation : jobsite.house.name)
    }
    if (abbreviation && jobsite.additionalAmenImportantAbbreviations && jobsite.additionalAmenImportantAbbreviations.length > 0) {
        result.push(...jobsite.additionalAmenImportantAbbreviations)
    }else if (!abbreviation && jobsite.additionalAmenImportantLabels && jobsite.additionalAmenImportantLabels.length > 0) {
        result.push(...jobsite.additionalAmenImportantLabels)
    }

    return result.join(', ')
}
